import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Download, Loader2, Upload } from "lucide-react";

import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { Card, CardContent } from "../../../../ui/card";
import { Checkbox } from "../../../../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../ui/popover";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { ScenarioHeader } from "../../../../components/portfolio-risk-concentration/ScenarioHeader";
import { asOfNote, ScenarioAsOfPicker } from "../../../../components/portfolio-risk-concentration/ScenarioAsOfPicker";
import { usd } from "../../../../components/portfolio-risk-concentration/format";
import { cn } from "../../../../utils/common/cn";
import { useCardCalculations } from "../../../../utils/portfolio-risk-concentration/api/card-calculations";
import {
  apiDetail,
  codeList,
  codesLabel,
  conditionSummary,
  DEFAULT_SCENARIO_CLAUSES,
  useApprovedScenarios,
  useResetScenarioAnswers,
  useOpenApprovedScenarioPool,
  useScenarioContracts,
  useScenarioInvestorCodes,
  useUploadScenarioContracts,
  type ScenarioApproved,
  type ScenarioApprovedStatus,
  type ScenarioCondition,
  type ScenarioUploadResult,
  type UploadContractRow,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";
import { downloadTemplate, parseContractCsv } from "../../../../utils/portfolio-risk-concentration/api/scenarioUpload";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";

const NEW_SCENARIO = "NEW";
const LOG_PAGE_SIZE = 10;

const STATUS_LABEL: Record<ScenarioApprovedStatus, string> = {
  APPROVED: "Approved",
  EXECUTED_CONFIRMED: "Reconciled",
  DISCREPANCY: "Discrepancy",
};
const STATUS_CLASS: Record<ScenarioApprovedStatus, string> = {
  APPROVED: "border-amber/60 text-amber",
  EXECUTED_CONFIRMED: "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
  DISCREPANCY: "border-destructive/50 text-destructive",
};

const destinationText = (code: string, facility: string | null) => `Code ${code}${facility ? ` · ${facility}` : ""}`;

/** One condition as the Scenario step shows it: its clauses, then where it sends the contracts ("Risk Rating = A & PG Flag = No → Code 52 · CAP1"). */
function conditionLine(condition: ScenarioCondition, contractCount?: number): string {
  const where = condition.destination
    ? destinationText(condition.destination.investorCode, condition.destination.facilityCode)
    : "Exclude";
  return `${conditionSummary(condition, contractCount).join(" & ")} → ${where}`;
}

/** The condition lines of an approved scenario. A scenario keeps a contract count per destination, not per condition, so a condition with no
 *  clauses shows its count only when it is the one condition routed to its destination; otherwise (or for Exclude, which has no destination)
 *  it still reads "Any contract". */
function conditionLines(scenario: ScenarioApproved, conditions: ScenarioCondition[]): string[] {
  return conditions.map((c) => {
    const dest = c.destination;
    const count =
      dest &&
      conditions.filter(
        (o) => o.destination?.investorCode === dest.investorCode && o.destination.facilityCode === dest.facilityCode,
      ).length === 1
        ? scenario.destinations.find((d) => d.investorCode === dest.investorCode && d.facilityCode === dest.facilityCode)?.contractCount
        : undefined;
    return conditionLine(c, count);
  });
}

/** The conditions an approved scenario opens with: the ones it was approved with, or -- for one approved before they were saved -- the
 *  default conditions routed to each destination it was approved with (one condition per destination), which is as close as what was
 *  recorded allows. The Scenario step lets the viewer change them. */
function openingConditions(scenario: ScenarioApproved): ScenarioCondition[] {
  if (scenario.conditions && scenario.conditions.length > 0) return scenario.conditions;
  return scenario.destinations.map((d) => ({
    clauses: DEFAULT_SCENARIO_CLAUSES,
    destination: { investorCode: d.investorCode, facilityCode: d.facilityCode },
  }));
}

/** The dates an approved scenario was built on, by how its pool was chosen: an uploaded file has an effective date and no booking range; a
 *  scenario built from investor codes has its booking range and the effective date it was read on. */
function scenarioDates(s: ScenarioApproved): string {
  if (s.sourceMode === "UPLOAD") return `Uploaded contracts · effective ${s.effectiveDate ?? "—"}`;
  const range = s.dateFrom && s.dateTo ? `${s.dateFrom} – ${s.dateTo}` : "—";
  return s.effectiveDate ? `${range} · effective ${s.effectiveDate}` : range;
}

/** A multi-select of investor codes: a button showing what is picked, opening a list of checkboxes, with "All investor codes" on top to pick
 *  every code at once (or clear them). Unticking the last code by hand is refused, so a pick is never left empty by accident. */
function SourceCodesSelect({
  options,
  selected,
  onChange,
  disabled,
}: {
  options: { code: string; codeMapping: string | null }[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const known = new Set(options.map((o) => o.code));
  // a code that is picked but not in the listing (an earlier scenario's, or an uploaded file's) stays visible and can be unpicked
  const rows = [...options, ...selected.filter((c) => !known.has(c)).map((code) => ({ code, codeMapping: null }))];
  const toggle = (code: string, on: boolean) => {
    const next = on ? [...selected, code] : selected.filter((c) => c !== code);
    if (next.length > 0) onChange(next);
  };
  const allCodes = options.map((o) => o.code);
  const allPicked = allCodes.length > 0 && allCodes.every((c) => selected.includes(c));
  // All on: every listed code (plus any picked code the listing does not have, so an earlier scenario's is not lost); off: none, to pick
  // from scratch.
  const toggleAll = (on: boolean) => onChange(on ? [...new Set([...allCodes, ...selected])] : []);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label="Investor Codes"
          className="mt-1 h-10 w-full justify-between font-normal"
        >
          <span className="truncate">
            {selected.length === 0
              ? "Select investor codes"
              : allPicked
                ? `All investor codes (${selected.length})`
                : selected.length <= 3
                  ? selected.join(", ")
                  : `${selected.length} investor codes`}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-72 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1">
        {allCodes.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2 rounded-sm border-b border-border px-2 py-1.5 text-sm font-medium hover:bg-muted">
            <Checkbox checked={allPicked} onCheckedChange={(on) => toggleAll(on === true)} />
            <span className="truncate">All investor codes</span>
          </label>
        )}
        {rows.map((o) => (
          <label key={o.code} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted">
            <Checkbox checked={selected.includes(o.code)} onCheckedChange={(on) => toggle(o.code, on === true)} />
            <span className="truncate">{o.codeMapping ? `${o.code} — ${o.codeMapping}` : o.code}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** 1 · Scenario Portfolio — pick the source pool and the booking window, or open an earlier approved scenario with those (and its
 *  conditions and destinations) already filled in. The approved log is the last segment of the page. */
export default function ScenarioPortfolioStep({
  draft,
  patch,
  base,
}: {
  draft: ScenarioDraft;
  patch: (p: Partial<ScenarioDraft>) => void;
  base: string;
}) {
  const navigate = useNavigate();
  const codesQ = useScenarioInvestorCodes();
  const approvedQ = useApprovedScenarios();

  // The investor codes the pool is taken from: one or several (a scenario may move contracts out of more than one code).
  const [sourceCodes, setSourceCodes] = useState<string[]>(() => {
    const kept = codeList(draft.sourceInvestorCode);
    return kept.length > 0 ? kept : ["10"];
  });
  const sourceCode = sourceCodes.join(",");
  const [dateFrom, setDateFrom] = useState(draft.dateFrom);
  const [dateTo, setDateTo] = useState(draft.dateTo);
  const [approvedRef, setApprovedRef] = useState(NEW_SCENARIO);
  const [logOffset, setLogOffset] = useState(0);
  // The scenario's effective date: the contracts are read as they stood in that day's snapshot, and Monitor and Snapshot are computed on the
  // same day. A pool built from investor codes also has a booking range, which can't reach past this date (a contract booked after it is not
  // in that day's data); an uploaded file has no booking range at all.
  const [asOfDate, setAsOfDate] = useState(draft.asOfDate);

  // An uploaded contract list (Contract Number + Contract Schedule Number + the investor code each is under on the effective date) as the source pool, instead of a code and a window.
  const uploadMut = useUploadScenarioContracts();
  const fileRef = useRef<HTMLInputElement>(null);
  // the rows as read from the file, kept so a new effective date can check them again without picking the file twice
  const [uploadRows, setUploadRows] = useState<UploadContractRow[] | null>(null);
  // `scenarioRef`: the pool is an approved scenario's saved contract list, not a file picked here
  const [upload, setUpload] = useState<{ fileName: string; result: ScenarioUploadResult; scenarioRef?: string } | null>(null);
  const openPoolMut = useOpenApprovedScenarioPool();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const uploaded = upload?.result ?? null;
  // Usable when contracts were accepted; they may sit under several investor codes (the scenario takes contracts from all of them).
  const uploadOk = !!uploaded && uploaded.contractCount > 0 && uploaded.sourceInvestorCode !== null;
  const todayIso = new Date().toISOString().slice(0, 10);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploadError(null);
    try {
      const rows = parseContractCsv(await file.text());
      const result = await uploadMut.mutateAsync({ rows, fileDate: asOfDate });
      setUploadRows(rows);
      setUpload({ fileName: file.name, result });
      setApprovedRef(NEW_SCENARIO);
      setShowIssues(false);
    } catch (err) {
      setUploadError(apiDetail(err, "Could not read the file."));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function clearUpload() {
    setUpload(null);
    setUploadRows(null);
    setUploadError(null);
    setShowIssues(false);
  }

  /** A new effective date: an uploaded file is checked again against that day's snapshot (a contract can be Active, Owned or under another
   *  code on one day and not on another); a code and window reload through the contracts query, which carries the date. */
  async function changeAsOf(next: string) {
    setAsOfDate(next);
    // a booking range cannot reach past the effective date
    setDateFrom((d) => (d > next ? next : d));
    setDateTo((d) => (d > next ? next : d));
    if (!upload) return;
    setUploadError(null);
    try {
      if (upload.scenarioRef) {
        // an approved scenario's saved list, read on another day
        const result = await openPoolMut.mutateAsync({ scenarioRef: upload.scenarioRef, fileDate: next });
        setUpload({ ...upload, result });
      } else if (uploadRows) {
        const result = await uploadMut.mutateAsync({ rows: uploadRows, fileDate: next });
        setUpload({ fileName: upload.fileName, result });
      }
      setShowIssues(false);
    } catch (err) {
      setUploadError(apiDetail(err, "Could not check the file on that date."));
    }
  }

  const approved = useMemo(() => approvedQ.data?.scenarios ?? [], [approvedQ.data]);
  // Only a scenario whose date range is known can be opened again (see ScenarioApproved.restorable).
  const restorable = useMemo(() => approved.filter((s) => s.restorable), [approved]);
  const chosen: ScenarioApproved | null =
    approvedRef === NEW_SCENARIO ? null : (restorable.find((s) => s.scenarioRef === approvedRef) ?? null);

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(from.toISOString().slice(0, 10));
      setDateTo(today.toISOString().slice(0, 10));
    }
    // Only seed a default once — never overwrite a value the viewer already changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The booking range never reaches past the effective date (whichever of the two moved last, and whatever seeded the default).
  useEffect(() => {
    setDateFrom((d) => (d > asOfDate ? asOfDate : d));
    setDateTo((d) => (d > asOfDate ? asOfDate : d));
  }, [asOfDate]);

  /** Picks an approved scenario (or back to a new one), and opens it the way it was built:
   *   - from an UPLOADED file: its saved contracts, read on its effective date, and no booking range;
   *   - from investor codes: its codes and booking range, on the effective date it was read on.
   *  Its conditions and destinations follow it into the Scenario step when it starts. They stay editable. */
  async function choose(ref: string) {
    setApprovedRef(ref);
    const scenario = restorable.find((s) => s.scenarioRef === ref);
    if (!scenario) {
      // back to a new scenario: a pool that was an approved scenario's goes with it (a file picked here stays)
      if (upload?.scenarioRef) clearUpload();
      return;
    }
    clearUpload();
    setSourceCodes(codeList(scenario.sourceInvestorCode));
    if (scenario.sourceMode === "UPLOAD") {
      try {
        const result = await openPoolMut.mutateAsync({ scenarioRef: scenario.scenarioRef });
        setAsOfDate(result.fileDate ?? scenario.effectiveDate ?? asOfDate);
        setUpload({ fileName: `Approved scenario ${scenario.label}`, result, scenarioRef: scenario.scenarioRef });
      } catch (err) {
        setApprovedRef(NEW_SCENARIO);
        setUploadError(apiDetail(err, "Could not open this scenario's uploaded contracts."));
      }
      return;
    }
    // built from investor codes: the booking range, and the effective date it was read on (none recorded on an older one: keep the current)
    const effective = scenario.effectiveDate ?? asOfDate;
    if (scenario.effectiveDate) setAsOfDate(scenario.effectiveDate);
    if (scenario.dateFrom) setDateFrom(scenario.dateFrom > effective ? effective : scenario.dateFrom);
    if (scenario.dateTo) setDateTo(scenario.dateTo > effective ? effective : scenario.dateTo);
  }

  // With an upload the source code, the window and the booked value all come from the uploaded contracts (the code and dates below follow the
  // file and cannot be edited); otherwise they are the code and window chosen here.
  const effSource = uploadOk ? uploaded!.sourceInvestorCode! : sourceCode;
  const effFrom = uploadOk ? (uploaded!.dateFrom ?? todayIso) : dateFrom;
  const effTo = uploadOk ? (uploaded!.dateTo ?? todayIso) : dateTo;

  const resetAnswers = useResetScenarioAnswers();
  const previewQ = useScenarioContracts(
    !upload && sourceCodes.length > 0 && dateFrom && dateTo ? { investorCode: sourceCode, dateFrom, dateTo, fileDate: asOfDate } : null,
  );
  // The snapshot actually read, which differs from the date picked only when there is no data file for that day.
  const resolvedFileDate = (upload ? uploaded?.fileDate : previewQ.data?.fileDate) ?? undefined;

  // The real book total -- the Capacity page's "Total portfolio" card: the whole Owned book ('All Facility',
  // all investors, Loans & Leases) at net investment, from card_calculations via /card-calculations. It used to
  // come from /facility-capacity?facility=EXTERNAL, which scans business_performance_daily several times per
  // request and was timing out. Passed into the draft on Start so ScenarioApplyStep's scenarioTotal has a
  // real number too.
  const capacityQ = useCardCalculations("ALL", asOfDate, "ALL", "ALL");
  const existingPortfolioDollars = capacityQ.data?.cards?.totalPortfolio ?? null;

  // The contracts of the chosen investor code and date range. isFetching, not just isLoading: a range that was fetched before shows its
  // cached contracts at once while they are fetched again, and Start must wait for that fresh load, not run on the old copy.
  const previewLoading = !upload && (previewQ.isLoading || previewQ.isFetching);
  const bookedDollars = uploaded ? uploaded.dollarTotal : (previewQ.data?.dollarTotal ?? 0);
  const bookedCount = uploaded ? uploaded.contractCount : previewQ.data?.contractCount;
  const canStart = upload ? uploadOk && !uploadMut.isPending : !previewLoading && !!previewQ.data && previewQ.data.contractCount > 0;

  async function start() {
    if (!canStart) return;
    // Starting a scenario starts from nothing kept: no Monitor / Snapshot answer and no filter choice from an earlier one.
    resetAnswers();
    if (uploaded && uploadOk) {
      // The uploaded contracts ARE the pool: the keys go with the draft so Monitor and Snapshot use exactly these, not the whole code and window.
      patch({
        sourceInvestorCode: effSource,
        dateFrom: effFrom,
        dateTo: effTo,
        contracts: uploaded.contracts,
        sourceKeys: uploaded.contracts.map((c) => c.contractScheduleKey),
        asOfDate: uploaded.fileDate ?? asOfDate,
        poolFileDate: uploaded.fileDate ?? asOfDate,
        // an approved scenario brings its own conditions; a file starts empty and the Scenario step seeds it
        conditions: chosen ? openingConditions(chosen) : [],
        approvedScenarioLabel: chosen?.label ?? null,
        waterfall: null,
        monitorView: null,
        snapshotView: null,
        existingPortfolioDollars,
      });
      navigate(`${base}/apply`);
      return;
    }
    if (!previewQ.data) return;
    patch({
      sourceInvestorCode: sourceCode,
      dateFrom,
      dateTo,
      contracts: previewQ.data.contracts,
      sourceKeys: null,
      asOfDate,
      poolFileDate: previewQ.data.fileDate ?? asOfDate,
      // An approved scenario brings its own conditions (each with its destination); a new one starts empty and the Scenario step seeds it.
      conditions: chosen ? openingConditions(chosen) : [],
      approvedScenarioLabel: chosen?.label ?? null,
      waterfall: null,
      monitorView: null,
      snapshotView: null,
      existingPortfolioDollars,
    });
    navigate(`${base}/apply`);
  }

  const logPage = approved.slice(logOffset, logOffset + LOG_PAGE_SIZE);

  return (
    <div>
      <ScenarioHeader title="Scenario Portfolio" />
      <p className="pt-2 max-w-2xl text-sm text-muted-foreground">
        Select contracts sitting in an unassigned investor code (or upload the exact contracts to
        move), assign them to a facility, and see the effect on your real portfolio's limits —
        before anything is proposed or made real.
      </p>

      <Card className="mt-4">
        <CardContent className="pt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Start from
          </p>
          <div className="mt-2 max-w-xl">
            <Select value={approvedRef} onValueChange={(v) => void choose(v)}>
              <SelectTrigger aria-label="Start from" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW_SCENARIO}>New scenario</SelectItem>
                {restorable.map((s) => (
                  <SelectItem key={s.scenarioRef} value={s.scenarioRef}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {approvedQ.isLoading
                ? "Loading approved scenarios…"
                : restorable.length === 0
                  ? "No earlier approved scenario can be opened yet. Scenarios approved from now on can."
                  : "Or open an earlier approved scenario: its investor code, date range, conditions and destinations are filled in, and you can still change them."}
            </p>
          </div>
          {chosen && (
            <div className="mt-3 space-y-1 text-xs">
              <p className="text-muted-foreground">
                {codesLabel(chosen.sourceInvestorCode)} · {scenarioDates(chosen)} ·{" "}
                {chosen.destinations.map((d) => destinationText(d.investorCode, d.facilityCode)).join(" + ")}
              </p>
              {conditionLines(chosen, openingConditions(chosen)).map((line, i) => (
                <p key={i} className="text-foreground">
                  {i + 1}. {line}
                </p>
              ))}
              {!chosen.conditionsRecorded && (
                <p className="text-amber">
                  This scenario was approved before its conditions were saved, so it opens on the default conditions and its approved
                  destination(s). Adjust them in the next step.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-3">
        <CardContent className="pt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Upload contracts</p>
          <p className="mt-2 max-w-2xl text-xs text-muted-foreground">
            Pre-select the contracts to move instead of picking a pool: upload a CSV with three columns, Contract Number, Contract Schedule Number
            and the Investor Code each contract is under on the effective date. The Contract Schedule (the Contract Number and its schedule
            number joined, like 20086710_1) is worked out for you. Each row is one contract schedule, so a contract with several schedules
            needs a row for each one you want to move. One file can hold contracts
            from several investor codes. The source codes, the booked value and the date range below follow the file, so you can check the
            count and value of what you uploaded.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <Button variant="outline" size="sm" disabled={uploadMut.isPending} onClick={() => fileRef.current?.click()}>
              {uploadMut.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              {uploadMut.isPending ? "Checking contracts…" : "Upload contracts"}
            </Button>
            <Button variant="ghost" size="sm" onClick={downloadTemplate}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download template
            </Button>
            {upload && (
              <Button variant="ghost" size="sm" onClick={clearUpload}>
                Clear upload
              </Button>
            )}
          </div>
          <div className="mt-6 max-w-2xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Effective Date</p>
            <p className="mt-2 text-xs text-muted-foreground">
              The scenario is built on the portfolio as it stood on this effective date, today or any earlier one. The contracts selected, from
              investor codes, an approved scenario or an uploaded file, are the ones that were Active, Owned and Eligible on this effective
              date. The booking date range narrows a pool built from investor codes; an uploaded file does not use it.
            </p>
            <div className="mt-3">
              <ScenarioAsOfPicker value={asOfDate} onChange={(d) => void changeAsOf(d)} disabled={uploadMut.isPending} />
            </div>
            {asOfNote(asOfDate, resolvedFileDate) && (
              <p className="mt-2 text-[11px] text-amber">{asOfNote(asOfDate, resolvedFileDate)}</p>
            )}
          </div>
          {uploadError && <p className="mt-2 text-xs text-destructive">{uploadError}</p>}
          {upload && uploaded && (
            <div className="mt-3 space-y-2 text-xs">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">{upload.fileName}</span> · {uploaded.uploadedRows} rows ·{" "}
                <span className="text-foreground">{uploaded.contractCount} contracts accepted</span>
                {uploaded.issueCount > 0 && <span className="text-amber"> · {uploaded.issueCount} not accepted</span>}
              </p>
              {Object.keys(uploaded.sourceInvestorCodes).length > 0 && (
                <p className="text-muted-foreground">
                  Under {Object.keys(uploaded.sourceInvestorCodes).length === 1 ? "investor code" : "investor codes"}{" "}
                  {Object.entries(uploaded.sourceInvestorCodes)
                    .map(([code, n]) => `${code} (${n})`)
                    .join(", ")}
                  .
                </p>
              )}
              {uploaded.contractCount === 0 && (
                <p className="text-destructive">None of the rows could be used, so there is nothing to start a scenario from.</p>
              )}
              {uploaded.issueCount > 0 && (
                <div>
                  <button type="button" className="text-primary hover:underline" onClick={() => setShowIssues((v) => !v)}>
                    {showIssues ? "Hide" : "Show"} the rows that were not accepted
                  </button>
                  {showIssues && (
                    <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">Row</TableHead>
                            <TableHead>Contract Number</TableHead>
                            <TableHead>Contract Schedule</TableHead>
                            <TableHead>Investor Code</TableHead>
                            <TableHead>Why</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {uploaded.issues.map((i, k) => (
                            <TableRow key={`${i.row}-${k}`}>
                              <TableCell className="text-xs">{i.row ?? "—"}</TableCell>
                              <TableCell className="whitespace-nowrap text-xs">{i.contractNumber || "—"}</TableCell>
                              <TableCell className="whitespace-nowrap text-xs">{i.contractScheduleKey || i.contractScheduleNumber || "—"}</TableCell>
                              <TableCell className="text-xs">{i.investorCode || "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{i.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {uploaded.issueCount > uploaded.issues.length && (
                        <p className="px-3 py-2 text-[11px] text-muted-foreground">
                          Showing the first {uploaded.issues.length} of {uploaded.issueCount}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Source
            </p>
            <div className="mt-2">
              <Label>Investor Codes</Label>
              <SourceCodesSelect
                options={codesQ.data ?? []}
                selected={uploadOk ? codeList(effSource) : sourceCodes}
                onChange={setSourceCodes}
                disabled={uploadOk}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {uploadOk
                  ? "Set by the uploaded contracts."
                  : "Pick one or more investor codes. Unassigned pool — new originations not yet pledged to a facility."}
              </p>
            </div>
            <div className="mt-4 flex items-baseline gap-4">
              <div>
                <p className="text-lg font-semibold text-amber">
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    usd(bookedDollars)
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground">{upload ? "in the uploaded contracts" : "booked in selected window"}</p>
              </div>
              <div>
                <p className="text-lg font-semibold">
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    (bookedCount ?? "—")
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground">contracts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid grid-cols-2 gap-3 pt-6">
            <p className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Booking Date Range
            </p>
            <div>
              <Label htmlFor="scn-from">From</Label>
              <Input
                id="scn-from"
                type="date"
                className="mt-1 [&::-webkit-calendar-picker-indicator]:invert"
                value={uploadOk ? "" : dateFrom}
                max={asOfDate}
                disabled={uploadOk}
                onChange={(e) => setDateFrom(e.target.value > asOfDate ? asOfDate : e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="scn-to">To</Label>
              <Input
                id="scn-to"
                type="date"
                className="mt-1 [&::-webkit-calendar-picker-indicator]:invert"
                value={uploadOk ? "" : dateTo}
                max={asOfDate}
                disabled={uploadOk}
                onChange={(e) => setDateTo(e.target.value > asOfDate ? asOfDate : e.target.value)}
              />
            </div>
            <p className="col-span-2 text-[11px] text-muted-foreground">
              {uploadOk
                ? "Not used with an uploaded file: the file names the contracts, and the effective date says which day's data to read them on."
                : "Narrows the pool to contracts booked in this window — applied before any other condition. It cannot go past the effective date: a contract booked after it is not in that day's data."}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-3">
        <CardContent className="flex items-center justify-between py-4">
          <span className="text-xs text-muted-foreground">Existing Portfolio</span>
          <span className="text-sm font-semibold">
            {capacityQ.isLoading ? (
              <Loader2 className="inline h-4 w-4 animate-spin text-muted-foreground" />
            ) : existingPortfolioDollars !== null ? (
              usd(existingPortfolioDollars)
            ) : (
              "—"
            )}
          </span>
        </CardContent>
      </Card>

      <div className="mt-4 flex justify-end">
        <Button disabled={!canStart} onClick={start}>
          {previewLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading contracts…
            </>
          ) : chosen ? (
            "Open Scenario →"
          ) : (
            "Start Scenario →"
          )}
        </Button>
      </div>

      {/* The approved log -- the last segment of the page. */}
      <div className="mt-8">
        <p className="text-sm font-semibold">Approved Scenarios</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Every scenario that has been approved, newest first. Open one to review it again in Scenario Monitor and Scenario Snapshot.
        </p>
        <div className="mt-2 overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Scenario</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead className="text-right">Contracts</TableHead>
                <TableHead className="text-right">Financed Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>State</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                    Loading approved scenarios…
                  </TableCell>
                </TableRow>
              )}
              {!approvedQ.isLoading && approved.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                    No approved scenarios yet.
                  </TableCell>
                </TableRow>
              )}
              {logPage.map((s) => (
                <TableRow
                  key={s.scenarioRef}
                  className={cn(s.scenarioRef === approvedRef && "bg-secondary/40", s.scenarioStatus === "RETIRED" && "opacity-60")}
                >
                  <TableCell className="whitespace-nowrap text-xs font-medium">{s.label}</TableCell>
                  <TableCell className="text-xs">{codesLabel(s.sourceInvestorCode)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {scenarioDates(s)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.conditions && s.conditions.length > 0 ? (
                      conditionLines(s, s.conditions).map((line, i) => (
                        <p key={i}>
                          {i + 1}. {line}
                        </p>
                      ))
                    ) : (
                      <span className="text-muted-foreground">
                        Not recorded (approved before conditions were saved)
                        {s.restorable ? " — opens on the default conditions" : ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{s.contractCount}</TableCell>
                  <TableCell className="text-right">{usd(s.dollarTotal)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-[10px] uppercase tracking-wider", STATUS_CLASS[s.status])}>
                      {STATUS_LABEL[s.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {/* Active until a request to retire it is approved; nothing is ever deleted. */}
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase tracking-wider",
                        s.scenarioStatus === "RETIRED" ? "text-muted-foreground" : "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
                      )}
                    >
                      {s.scenarioStatus === "RETIRED" ? "Retired" : "Active"}
                    </Badge>
                    {s.retirePending && <p className="mt-1 text-[10px] text-amber">Retire requested</p>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      disabled={!s.restorable}
                      title={
                        s.scenarioStatus === "RETIRED"
                          ? "This scenario is retired, so it cannot be opened again."
                          : s.sourceMode === "UPLOAD"
                            ? s.restorable
                              ? undefined
                              : "This scenario started from an uploaded contract list that was not saved, so it cannot be opened again."
                            : s.restorable
                              ? undefined
                              : "This scenario was approved before its date range was saved, so it cannot be opened again."
                      }
                      onClick={() => {
                        void choose(s.scenarioRef);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {s.scenarioRef === approvedRef ? "Selected" : "Open"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
            <span>
              {approved.length === 0
                ? "0 of 0"
                : `${logOffset + 1}–${Math.min(logOffset + LOG_PAGE_SIZE, approved.length)} of ${approved.length}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={logOffset === 0}
                onClick={() => setLogOffset((o) => Math.max(0, o - LOG_PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={logOffset + LOG_PAGE_SIZE >= approved.length}
                onClick={() => setLogOffset((o) => o + LOG_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
